import { MapPin, Send, Phone } from 'lucide-react';

import type { SiteContent } from '../../content/types';
import { buildMailto } from '../../lib/utils';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Container } from '../layout/container';
import { SectionHeading } from './section-heading';
import { OpeningHoursCard } from './opening-hours-card';

interface ContactTeaserSectionProps {
  content: SiteContent;
}

export function ContactTeaserSection({ content }: ContactTeaserSectionProps): JSX.Element {
  const mailto = buildMailto(content.contact.emailHref, content.contact.mailInquiry.subject, content.contact.mailInquiry.body);

  return (
    <section className="py-12 sm:py-16">
      <Container className="grid gap-4 xl:grid-cols-[1.2fr_0.8fr]">
        <Card className="bg-white">
          <CardHeader className="space-y-3">
            <SectionHeading title={content.contactPage.heroTitle} description={content.contactPage.heroText} />
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <Card className="bg-[color:var(--color-surface-alt)]">
                <CardContent className="space-y-2 p-4">
                  <p className="inline-flex items-center gap-2 text-sm font-semibold text-[color:var(--color-text)]">
                    <MapPin className="h-4 w-4 text-[color:var(--color-primary)]" />
                    {`${content.contact.addressLine1}, ${content.contact.postalCode} ${content.contact.city}`}
                  </p>
                  <Button asChild size="sm" variant="outline" className="w-full">
                    <a href={content.contact.googleMapsUrl} target="_blank" rel="noreferrer">
                      {content.contact.googleMapsLabel}
                    </a>
                  </Button>
                </CardContent>
              </Card>

              <Card className="bg-[color:var(--color-surface-alt)]">
                <CardContent className="space-y-2 p-4">
                  <p className="inline-flex items-center gap-2 text-sm font-semibold text-[color:var(--color-text)]">
                    <Send className="h-4 w-4 text-[color:var(--color-primary)]" />
                    {content.contact.emailDisplay}
                  </p>
                  <Button asChild size="sm" className="w-full">
                    <a href={mailto}>{content.contact.mailInquiry.label}</a>
                  </Button>
                  <Button asChild size="sm" variant="outline" className="w-full">
                    <a href={content.contact.phoneHref}>
                      <Phone className="h-4 w-4" />
                      {content.contact.phoneDisplay}
                    </a>
                  </Button>
                </CardContent>
              </Card>
            </div>
          </CardContent>
        </Card>

        <OpeningHoursCard content={content} />
      </Container>
    </section>
  );
}
